import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Col, Modal, Row, Spinner, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { rockTasksApi } from "../../services/rockTasksApi";
import { taskResultDisplayName, normalizeIdentifyTaskResult } from "../../utils/normalizeIdentifyTaskResult";
import ErrorAlert from "../common/ErrorAlert";
import TaskRecognitionResult from "./TaskRecognitionResult";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function formatMs(ms) {
  if (ms == null) return "—";
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${ms}ms`;
}

const CLIENT_DEVICE_INFO_FIELDS = [
  { key: "os", label: "OS" },
  { key: "os_version", label: "OS Version" },
  { key: "app_version", label: "App Version" },
  { key: "app_build", label: "App Build" },
  { key: "email", label: "Email" },
  { key: "device_id", label: "Device ID" },
  { key: "rc_app_user_id", label: "RC App User ID" },
  { key: "country_code", label: "Country Code" },
  { key: "language_code", label: "Language Code" },
  { key: "device_model", label: "Device Model" },
  { key: "device_manufacturer", label: "Device Manufacturer" },
  { key: "network_wifi", label: "Network WiFi" },
  { key: "network_cellular", label: "Network Cellular" },
  { key: "network_carrier", label: "Network Carrier" },
  { key: "screen_width", label: "Screen Width" },
  { key: "screen_height", label: "Screen Height" }
];

function formatDeviceInfoValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function getDeviceInfoFieldValue(deviceInfo, key) {
  if (!deviceInfo || typeof deviceInfo !== "object") return null;
  if (deviceInfo[key] != null && deviceInfo[key] !== "") return formatDeviceInfoValue(deviceInfo[key]);

  const aliases = {
    device_model: ["model"],
    app_version: ["version"]
  };
  const altKeys = aliases[key] || [];
  for (const altKey of altKeys) {
    if (deviceInfo[altKey] != null && deviceInfo[altKey] !== "") {
      return formatDeviceInfoValue(deviceInfo[altKey]);
    }
  }
  return null;
}

function hasClientDeviceContext(task) {
  return Boolean(task?.client_device_info || task?.client_device_id || task?.client_country_code);
}

function prettyJson(value) {
  if (value == null) return null;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseJsonValue(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function InfoRow({ label, value }) {
  return (
    <tr>
      <td className="text-muted small" style={{ width: 180, whiteSpace: "nowrap" }}>
        {label}
      </td>
      <td style={{ wordBreak: "break-all" }}>{value ?? "—"}</td>
    </tr>
  );
}

function statusBadge(status) {
  if (status === "completed") return <Badge bg="success">completed</Badge>;
  if (status === "failed") return <Badge bg="danger">failed</Badge>;
  if (status === "processing") return <Badge bg="warning" text="dark">processing</Badge>;
  return <Badge bg="secondary">{status}</Badge>;
}

function taskIsVpn(task) {
  return Boolean(task?.attributes?.is_vpn);
}

export default function RockTaskV2DetailModal({ show, onHide, taskId }) {
  const [task, setTask] = useState(null);
  const [events, setEvents] = useState([]);
  const [iterations, setIterations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("timeline"); // "result" | "timeline"
  const [taskIdCopied, setTaskIdCopied] = useState(false);
  const taskIdCopyTimerRef = useRef(null);

  useEffect(() => {
    if (!show || !taskId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [taskData, eventsData] = await Promise.all([
          rockTasksApi.getV2TaskDetail(taskId),
          rockTasksApi.getTaskEvents(taskId)
        ]);
        if (!cancelled) {
          setTask(taskData);
          setEvents(eventsData?.events ?? []);
          setIterations(eventsData?.iterations ?? {});
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [show, taskId]);

  useEffect(() => {
    return () => {
      if (taskIdCopyTimerRef.current) {
        clearTimeout(taskIdCopyTimerRef.current);
        taskIdCopyTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!show) return;
    // Default to timeline because it's the primary debug surface.
    setActiveView("timeline");
  }, [show, taskId]);

  async function copyTaskId() {
    const text = task?.task_id;
    if (!text) return;
    try {
      if (typeof navigator?.clipboard?.writeText === "function") {
        await navigator.clipboard.writeText(text);
        setTaskIdCopied(true);
        if (taskIdCopyTimerRef.current) clearTimeout(taskIdCopyTimerRef.current);
        taskIdCopyTimerRef.current = setTimeout(() => setTaskIdCopied(false), 1500);
      }
    } catch {
      // ignore (clipboard may be blocked in some contexts)
    }
  }

  function renderClientDeviceSection() {
    if (!hasClientDeviceContext(task)) return null;

    const deviceInfo = parseJsonValue(task?.client_device_info);
    const rows = CLIENT_DEVICE_INFO_FIELDS.map(({ key, label }) => {
      let value = getDeviceInfoFieldValue(deviceInfo, key);
      if (key === "device_id" && !value && task?.client_device_id) value = formatDeviceInfoValue(task.client_device_id);
      if (key === "country_code" && !value && task?.client_country_code) value = formatDeviceInfoValue(task.client_country_code);
      if (key === "language_code" && !value && task?.language_code) value = formatDeviceInfoValue(task.language_code);
      if (key === "app_version" && !value && task?.app_version) value = formatDeviceInfoValue(task.app_version);
      return { key, label, value };
    }).filter((r) => r.value != null);

    const rawDeviceInfoText = prettyJson(deviceInfo ?? task?.client_device_info);
    if (rows.length === 0 && !rawDeviceInfoText) return null;

    const midpoint = Math.ceil(rows.length / 2);
    const leftRows = rows.slice(0, midpoint);
    const rightRows = rows.slice(midpoint);

    function renderColumn(columnRows) {
      return (
        <div className="px-2 py-2">
          <Table size="sm" className="mb-0 table-borderless">
          <tbody>
            {columnRows.map((row) => (
              <InfoRow
                key={row.key}
                label={row.label}
                value={
                  row.key === "email" || row.key === "device_id" || row.key === "rc_app_user_id" ? (
                    <code className="small">{row.value}</code>
                  ) : (
                    row.value
                  )
                }
              />
            ))}
          </tbody>
          </Table>
        </div>
      );
    }

    return (
      <Card className="mb-3">
        <Card.Header>
          <strong>Client Device Info</strong>
        </Card.Header>
        <Card.Body className="p-0">
          {rows.length > 0 ? (
            <Row className="g-0">
              <Col md={6} style={{ borderRight: "1px solid #dee2e6" }}>
                {renderColumn(leftRows)}
              </Col>
              <Col md={6}>
                {renderColumn(rightRows)}
              </Col>
            </Row>
          ) : (
            <div className="px-3 py-3">
              <div className="text-muted small mb-2">Device info (raw)</div>
              <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {rawDeviceInfoText}
              </pre>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Task V2 — {taskId ? `${taskId.slice(0, 8)}…` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorAlert error={error} title="Failed to load task" />
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading...
          </div>
        )}
        {!loading && task && (
          <>
            <div className="d-flex justify-content-end mb-3">
              <ButtonGroup size="sm">
                <Button
                  variant={activeView === "result" ? "primary" : "outline-primary"}
                  onClick={() => setActiveView("result")}
                >
                  Stored Result
                </Button>
                <Button
                  variant={activeView === "timeline" ? "primary" : "outline-primary"}
                  onClick={() => setActiveView("timeline")}
                >
                  Events Timeline
                </Button>
              </ButtonGroup>
            </div>

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Task Info</strong>
                {statusBadge(task.status)}
              </Card.Header>
              <Card.Body className="p-0">
                <Row className="g-0">
                  <Col md={6}>
                    <Table size="sm" className="mb-0 table-borderless">
                      <tbody>
                        <InfoRow
                          label="Task ID"
                          value={
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{taskIdCopied ? "Copied" : "Click to copy"}</Tooltip>}
                            >
                              <code
                                role="button"
                                tabIndex={0}
                                onClick={copyTaskId}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") copyTaskId();
                                }}
                                style={{ cursor: "pointer" }}
                                title="Click to copy"
                              >
                                {task.task_id}
                              </code>
                            </OverlayTrigger>
                          }
                        />
                        <InfoRow label="Endpoint" value={<Badge bg="secondary">{task.endpoint}</Badge>} />
                        <InfoRow label="Platform" value={task.platform} />
                        <InfoRow label="App Version" value={task.app_version} />
                        <InfoRow label="Device Model" value={task.device_model} />
                        <InfoRow label="Language Code" value={task.language_code} />
                        <InfoRow label="Primary Vendor" value={task.primary_vendor} />
                        <InfoRow label="Final Vendor" value={task.final_vendor} />
                        <InfoRow
                          label="VPN (client)"
                          value={
                            taskIsVpn(task) ? (
                              <div>
                                <Badge bg="info" text="dark" className="me-2">
                                  Yes
                                </Badge>
                                <span className="text-muted small">
                                  Mobile reported VPN — backup vendor was selected instead of primary.
                                </span>
                              </div>
                            ) : (
                              <Badge bg="secondary">No</Badge>
                            )
                          }
                        />
                        <InfoRow
                          label="Fallback"
                          value={
                            task.fallback_used ? (
                              <Badge bg="warning" text="dark">
                                Yes — {task.fallback_reason || "unknown"}
                              </Badge>
                            ) : (
                              <Badge bg="secondary">No</Badge>
                            )
                          }
                        />
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <Table size="sm" className="mb-0 table-borderless">
                      <tbody>
                        <InfoRow label="Primary Attempts" value={task.primary_attempts} />
                        <InfoRow label="Backup Attempts" value={task.backup_attempts} />
                        <InfoRow label="Primary Duration" value={formatMs(task.primary_duration_ms)} />
                        <InfoRow label="Backup Duration" value={formatMs(task.backup_duration_ms)} />
                        <InfoRow label="Total Duration" value={formatMs(task.total_duration_ms)} />
                        <InfoRow label="Primary Final HTTP" value={task.primary_final_status_code} />
                        <InfoRow label="Primary Final Error" value={task.primary_final_error} />
                        <InfoRow label="Event Count" value={task.event_count} />
                        <InfoRow label="Created" value={formatDate(task.created_at)} />
                        <InfoRow label="Updated" value={formatDate(task.updated_at)} />
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {(task.user_photo || task.image_url) && (
              <Card className="mb-3">
                <Card.Header>
                  <strong>Image</strong>
                </Card.Header>
                <Card.Body>
                  <a
                    href={task.user_photo || task.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      alt="User upload"
                      src={task.user_photo || task.image_url}
                      style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 8, objectFit: "contain" }}
                    />
                  </a>
                </Card.Body>
              </Card>
            )}

            {renderClientDeviceSection()}

            {activeView === "result" ? (
              <>
                {task.status === "failed" && (task.error_code || task.error_message) && (
                  <Card className="mb-3 border-danger">
                    <Card.Header className="bg-danger text-white">
                      <strong>Error</strong>
                    </Card.Header>
                    <Card.Body>
                      {task.error_code && (
                        <div className="mb-2">
                          <Badge bg="danger">{task.error_code}</Badge>
                          {task.error_origin && (
                            <span className="text-muted small ms-2">{task.error_origin}</span>
                          )}
                        </div>
                      )}
                      <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                        {task.error_message || task.client_error_message || "—"}
                      </pre>
                    </Card.Body>
                  </Card>
                )}

                {(task.status === "completed" || task.status === "done") && (
                  <div className="mb-4">
                    {(() => {
                      const preview = normalizeIdentifyTaskResult(task);
                      const label = taskResultDisplayName(preview);
                      if (preview.kind !== "empty") {
                        return (
                          <div className="text-muted small mb-2">
                            Stored result:{" "}
                            <Badge
                              bg={preview.kind === "gem" ? "success" : "warning"}
                              text={preview.kind === "gem" ? undefined : "dark"}
                            >
                              {preview.kind === "gem" ? "Gem (identify format)" : "Unified"}
                            </Badge>
                            {label !== "—" && <span className="ms-2">{label}</span>}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <TaskRecognitionResult task={task} />
                  </div>
                )}
              </>
            ) : (
              <>
                <h5 className="mb-2">Events Timeline ({events.length} events)</h5>
                {Object.keys(iterations).length > 0 && (
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    {Object.entries(iterations).map(([vendor, count]) => (
                      <Badge key={vendor} bg="light" text="dark" className="border">
                        {vendor} {count} attempts
                      </Badge>
                    ))}
                  </div>
                )}
                {events.length === 0 ? (
                  <p className="text-muted">No events.</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {events.map((ev) => (
                      <Card key={ev.id} style={{ borderLeft: "3px solid #0d6efd" }}>
                        <Card.Body className="py-2 px-3">
                          <div className="d-flex justify-content-between flex-wrap gap-2 mb-1">
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                              <Badge bg="secondary">{ev.stage}</Badge>
                              {ev.attempt != null && (
                                <span className="text-muted small">attempt #{ev.attempt}</span>
                              )}
                              {ev.vendor_name && <Badge bg="primary">{ev.vendor_name}</Badge>}
                              {ev.action_group && (
                                <Badge bg={ev.action_group === "SUCCESS" ? "success" : "warning"} text="dark">
                                  {ev.action_group}
                                </Badge>
                              )}
                            </div>
                            <span className="text-muted small">{formatDate(ev.server_ts)}</span>
                          </div>
                          <div className="small d-flex flex-wrap gap-3">
                            {ev.http_status_code != null && (
                              <span>
                                HTTP{" "}
                                <Badge bg={ev.http_status_code === 200 ? "success" : "danger"}>
                                  {ev.http_status_code}
                                </Badge>
                              </span>
                            )}
                            {ev.duration_ms != null && (
                              <span className="text-muted">⏱ {formatMs(ev.duration_ms)}</span>
                            )}
                            {ev.via_proxy ? <Badge bg="info">via proxy</Badge> : null}
                            {ev.oxylabs_session_id && (
                              <span className="text-muted">
                                session: <code>{ev.oxylabs_session_id}</code>
                              </span>
                            )}
                            {ev.error_code && (
                              <span>
                                <Badge bg="danger">{ev.error_code}</Badge>
                              </span>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
